import { useState, useRef } from 'react';
import {
  keyboardDemo,
  keyboardSpecialLookup,
} from '../content/demos/keyboardDemo';
import { TerminalWindow } from './TerminalWindow';

interface KeyInfo {
  key: string;
  bytes: string;
  sequence: string;
  desc: string;
}

export function KeyboardDemo() {
  const [lastKey, setLastKey] = useState<KeyInfo | null>(null);
  const [history, setHistory] = useState<KeyInfo[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const special =
      keyboardSpecialLookup[e.code] || keyboardSpecialLookup[e.key];
    const keyInfo: KeyInfo = special
      ? {
          key: e.key,
          bytes: special.bytes,
          sequence: special.sequence,
          desc: special.desc,
        }
      : {
          key: e.key,
          bytes: e.key.charCodeAt(0).toString(16).padStart(2, '0'),
          sequence: e.key,
          desc: `${keyboardDemo.characterDescPrefix}'${e.key}'`,
        };

    if (special) e.preventDefault();
    setLastKey(keyInfo);
    setHistory((prev) => [keyInfo, ...prev].slice(0, 8));
  };

  return (
    <div className="space-y-6">
      <TerminalWindow>
        <div
          className="min-h-[180px] flex flex-col items-center justify-center cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          <input
            ref={inputRef}
            type="text"
            className="absolute opacity-0 pointer-events-none"
            onKeyDown={handleKeyDown}
          />
          {lastKey ? (
            <div className="text-center space-y-4">
              <div className="text-3xl font-medium text-terminal-fg">
                {lastKey.key === ' ' ? 'Space' : lastKey.key}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-terminal-bg p-3">
                  <div className="text-terminal-dim text-xs uppercase mb-1">
                    {keyboardDemo.labels.bytes}
                  </div>
                  <code className="text-terminal-yellow">{lastKey.bytes}</code>
                </div>
                <div className="bg-terminal-bg p-3">
                  <div className="text-terminal-dim text-xs uppercase mb-1">
                    {keyboardDemo.labels.sequence}
                  </div>
                  <code className="text-terminal-cyan">{lastKey.sequence}</code>
                </div>
              </div>
              <div className="text-terminal-muted text-sm">{lastKey.desc}</div>
            </div>
          ) : (
            <div className="text-terminal-muted text-center">
              <div className="text-base mb-1">
                {keyboardDemo.emptyState.title}
              </div>
              <div className="text-sm text-terminal-dim">
                {keyboardDemo.emptyState.subtitle}
              </div>
            </div>
          )}
        </div>
      </TerminalWindow>

      {history.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {history.map((k, i) => (
            <div
              key={i}
              className="bg-terminal-highlight border border-terminal-border px-3 py-1.5 text-sm"
              style={{ opacity: 1 - i * 0.1 }}
            >
              <span className="text-terminal-fg">
                {k.key === ' ' ? '␣' : k.key}
              </span>
              <span className="text-terminal-dim mx-2">→</span>
              <code className="text-terminal-yellow">{k.bytes}</code>
            </div>
          ))}
        </div>
      )}

      <div className="text-terminal-muted text-sm">
        {keyboardDemo.footerIntro}
        <code className="text-terminal-yellow">
          {keyboardDemo.codes.escPhrase}
        </code>{' '}
        {keyboardDemo.footerAfterArrowBytes}
        <code className="text-terminal-cyan">
          {keyboardDemo.codes.caretSequence}
        </code>
        {keyboardDemo.footerLiteralOutro}
      </div>
    </div>
  );
}
